import React, { useState } from 'react';
import { Card, CardHeader, CardBody, Collapse, Button } from 'reactstrap';
import { OnChangeCallback, IDictionary, OptionItem } from '../../../../../../util/UtilityTypes'
import BoolInput from '../../../../../misc/BoolInput';
import NullableIntInputCheckboxText from '../../../../../misc/NullableIntInputCheckboxText';
import IntInput from '../../../../../misc/IntInput';
import * as GoogleUser from '../../../../../../util/GoogleUserExportTypes';
import GoogleUserEmailAddressPolicy from './GoogleUserEmailAddressPolicy';
import GoogleUserStaffOrganizationalUnitPolicy from './GoogleUserStaffOrganizationalUnitPolicy';
import GoogleUserPasswordPolicy from './GoogleUserPasswordPolicy';
import GoogleUserGeneralSettings from './GoogleUserGeneralSettings';
import { update } from '../../../../../../util/Helper';
import GoogleUserCustomAttributes from './GoogleUserCustomAttributes';
import { useTranslation } from 'react-i18next';

const NAME = 'staffConfiguration';

interface GoogleUserExportStaffProps {
    configuration: GoogleUser.StaffConfiguration
    configurationType: string
    update: OnChangeCallback<GoogleUser.StaffConfiguration>
    validation: string[]
    disableInput: boolean
    sourceSystemOptions: OptionItem<string>[]
}

const GoogleUserExportStaff: React.FC<GoogleUserExportStaffProps> = (props) => {
    const { t } = useTranslation(['configuration']);

    const [isOpen, setOpen] = useState(initialIsOpen);

    const renderCard = (component: React.ReactNode, type: string, displayName: string) => {
        return (
            <Card>
                <CardHeader tag="h5">
                    <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setOpen({ ...isOpen, [type]: !isOpen[type] })}>{isOpen[type] ? '-' : '+'}</Button>
                    {displayName}
                </CardHeader>
                <Collapse isOpen={isOpen[type]}>
                    <CardBody>
                        {component}
                    </CardBody>
                </Collapse>
            </Card>
        );
    }

    return (
        <Card className="mb-3">
            <CardHeader tag="h4">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen({ ...isOpen, staff: !isOpen.staff })}>{isOpen["staff"] ? '-' : '+'}</Button>
                {t('googleUserExport.staff.title')}
            </CardHeader>
            <Collapse isOpen={isOpen["staff"]}>
                <CardBody className="multiple-cards">

                    <BoolInput
                        name={`${props.configurationType}-enabled`}
                        value={props.configuration.enabled}
                        disabled={props.disableInput}
                        title={t('googleUserExport.staff.configurationType')}
                        updateValue={(v, k) => update(NAME, props.configuration, { enabled: k }, props.update)}
                    />

                    {props.configuration.enabled && <>
                        {renderCard(
                            <GoogleUserGeneralSettings
                                generalSettings={{
                                    displayNamePolicy: props.configuration.displayNamePolicy,
                                    includeHomePhone: props.configuration.includeHomePhone,
                                    includeMobilePhone: props.configuration.includeMobilePhone,
                                    includeWorkPhone: props.configuration.includeWorkPhone,
                                    includeHomeAddress: props.configuration.includeHomeAddress,
                                    includeUserOrganisations: props.configuration.includeUserOrganisations,
                                }}
                                onChange={(n, v) => update(NAME, props.configuration, { ...v }, props.update)}
                                disabled={props.disableInput}
                                validation={props.validation}
                                suffixOptions={GoogleUser.staffDisplayNameSuffix.map(x => ({ value: x, text: t(`googleUserExport.generalSettings.displayName.suffix.staffDisplayNameSuffixOptions.${x}`) }))}
                                configurationType={props.configurationType}
                            />
                            , "displayName", t('googleUserExport.generalSettings.title'))}

                        {renderCard(
                            <GoogleUserEmailAddressPolicy
                                emailAddressPolicy={props.configuration.emailAddressPolicy}
                                onChange={(n, v) => update(NAME, props.configuration, { emailAddressPolicy: v }, props.update)}
                                disabled={props.disableInput}
                                validation={props.validation}
                                configurationType={props.configurationType}
                            />
                            , "email", t('googleUserExport.emailAddressPolicy.title'))}

                        {renderCard(
                            <GoogleUserStaffOrganizationalUnitPolicy
                                organizationalUnitPolicy={props.configuration.organizationalUnitPolicy}
                                onChange={(n, v) => update(NAME, props.configuration, { organizationalUnitPolicy: v }, props.update)}
                                disabled={props.disableInput}
                                validation={props.validation}
                                configurationType={props.configurationType}
                            />
                            , "ou", t('googleUserExport.OuPolicy.title'))}

                        {renderCard(
                            <GoogleUserPasswordPolicy
                                passwordPolicy={props.configuration.passwordPolicy}
                                onChange={(n, v) => update(NAME, props.configuration, { passwordPolicy: v }, props.update)}
                                disabled={props.disableInput}
                                validation={props.validation}
                                configurationType={props.configurationType}
                            />
                            , "password", t('googleUserExport.passwordPolicy.title'))}

                        {renderCard(
                            <GoogleUserCustomAttributes
                                customAttributes={props.configuration.customAttributes}
                                onChange={(n, v) => update(NAME, props.configuration, { customAttributes: v }, props.update)}
                                disabled={props.disableInput}
                                validation={props.validation}
                                configurationType={props.configurationType}
                                sourceSystemOptions={props.sourceSystemOptions}
                            />
                            , "attribute", t('googleUserExport.customAttributes.title'))}
                    </>}

                    {renderCard(<>
                        <h5>{t('googleUserExport.lifecycleManagement.archive.title')}</h5>
                        <IntInput
                            title={t('googleUserExport.lifecycleManagement.archive.text')}
                            name="archiveGracePeriod"
                            value={props.configuration.archiveGracePeriod}
                            disabled={props.disableInput}
                            updateValue={(n, v) => update(NAME, props.configuration, { archiveGracePeriod: v }, props.update)}
                            invalidFeedback={t('googleUserExport.lifecycleManagement.archive.invalidFeedback')}
                            invalid={props.validation.indexOf(`${props.configurationType}.archiveGracePeriod`) !== -1}
                        />
                        <h5>{t('googleUserExport.lifecycleManagement.delete.title')}</h5>
                        <NullableIntInputCheckboxText
                            name={`${props.configurationType}-deleteAfterDays`}
                            value={props.configuration.deleteAfterDays}
                            disabled={props.disableInput}
                            title={t('googleUserExport.lifecycleManagement.delete.text')}
                            defaultValue={365}
                            onChange={(n, v) => update(NAME, props.configuration, { deleteAfterDays: v }, props.update)}
                            checkboxTitle={t('googleUserExport.lifecycleManagement.delete.noDelete')}
                            invalidFeedback={t('googleUserExport.lifecycleManagement.delete.invalidFeedback')}
                            invalid={props.validation.indexOf(`${props.configurationType}.deleteAfterDays`) !== -1}
                        />
                    </>
                        , "lifecycle", t('googleUserExport.lifecycleManagement.title'))}

                </CardBody>
            </Collapse>
        </Card>);
}

const initialIsOpen: IDictionary<string, boolean> = {
    staff: true,
    displayName: true,
    email: true,
    ou: true,
    password: true,
    attribute: true,
    lifecycle: true,
}

export default GoogleUserExportStaff; 
